import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  Heart, 
  AlertTriangle, 
  CheckCircle2, 
  TrendingUp, 
  Brain,
  MessageCircle,
  Users,
  Target,
  Sparkles,
  Lock,
  Crown,
  Calendar,
  Scale,
  ArrowRight
} from 'lucide-react';

export default function CompatibilityProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [insights, setInsights] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'ideal-partner' | 'red-flags' | 'strategy'>('overview');
  const [missingSetup, setMissingSetup] = useState(false);

  // Check subscription status
  const subscription = localStorage.getItem('tension_subscription') || 'free';
  const isPremium = subscription !== 'free';

  useEffect(() => {
    const storedProfile = localStorage.getItem('tension_compatibility_profile');
    const storedInsights = localStorage.getItem('tension_compatibility_insights');
    
    if (storedProfile) {
      setProfile(JSON.parse(storedProfile));
    }
    
    if (storedInsights) {
      setInsights(JSON.parse(storedInsights));
    } else {
      setMissingSetup(true);
    }
  }, [navigate]);

  if (missingSetup) {
    return (
      <div className="min-h-screen tension-page-bg flex items-center justify-center px-6">
        <Card className="max-w-xl p-6 space-y-4 text-center">
          <h2 className="text-xl font-medium text-neutral-900">Complete singles onboarding first</h2>
          <p className="text-sm text-neutral-600">
            Your readiness profile is created during onboarding. Finish that flow, then come back here.
          </p>
          <div className="flex justify-center gap-3">
            <Button onClick={() => navigate('/singles-onboarding')}>Start onboarding</Button>
            <Button variant="outline" onClick={() => navigate('/home')}>Back home</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!profile || !insights) {
    return (
      <div className="min-h-screen tension-page-bg flex items-center justify-center">
        <p className="text-neutral-600">Loading your readiness profile...</p>
      </div>
    );
  }

  const formatValue = (key: string) => {
    const labels: Record<string, string> = {
      honesty: 'Honesty',
      loyalty: 'Loyalty',
      growth: 'Personal Growth',
      family: 'Family',
      career: 'Career Ambition',
      adventure: 'Adventure',
      stability: 'Stability',
      creativity: 'Creativity',
      spirituality: 'Spirituality',
      health: 'Health/Fitness',
      community: 'Community',
      independence: 'Independence',
    };
    return labels[key] || key;
  };

  const formatGoal = (key: string) => {
    const labels: Record<string, string> = {
      marriage: 'Marriage',
      children: 'Children',
      'no-children': 'No children',
      'career-growth': 'Career Growth',
      travel: 'Travel',
      'home-ownership': 'Home Ownership',
      entrepreneurship: 'Entrepreneurship',
      relocation: 'Relocation',
      education: 'Further Education',
      'financial-freedom': 'Financial Freedom',
    };
    return labels[key] || key;
  };

  const getAttachmentColor = (style: string) => {
    const colors: Record<string, string> = {
      secure: 'bg-green-100 text-green-800',
      anxious: 'bg-yellow-100 text-yellow-800',
      avoidant: 'bg-blue-100 text-blue-800',
      fearful: 'bg-purple-100 text-purple-800',
      unsure: 'bg-neutral-100 text-neutral-800',
    };
    return colors[style] || colors.unsure;
  };

  return (
    <div className="min-h-screen tension-page-bg">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/home')}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-600" />
          </button>
          <div>
            <h1 className="text-xl font-light">Your Relationship Readiness Profile</h1>
            <p className="text-sm text-neutral-600">Understanding your ideal relationship dynamics</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Found Someone CTA */}
        <Card className="p-4 bg-gradient-to-r from-pink-600 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Found someone special?</p>
              <p className="text-sm text-pink-100">Transition to Relationship Mode</p>
            </div>
            <Button
              onClick={() => navigate('/relationship-transition')}
              className="bg-white text-pink-600 hover:bg-pink-50"
              size="sm"
            >
              <Users className="w-4 h-4 mr-2" />
              Start Transition
            </Button>
          </div>
        </Card>

        {/* Tab Navigation */}
        <div className="flex gap-2 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: Sparkles },
            { id: 'ideal-partner', label: 'Ideal Dynamics', icon: Heart },
            { id: 'red-flags', label: 'Red & Green Flags', icon: AlertTriangle },
            { id: 'strategy', label: 'Connection Strategy', icon: Target },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === id
                  ? 'bg-violet-600 text-white'
                  : 'bg-white text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Core Identity */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center">
                  <Brain className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <h2 className="text-lg font-medium">Your Profile</h2>
                  <p className="text-sm text-neutral-600">Core identity and values</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-neutral-900">Attachment Style</p>
                  <Badge className={getAttachmentColor(profile.patterns.attachmentStyle)}>
                    {profile.patterns.attachmentStyle.charAt(0).toUpperCase() + profile.patterns.attachmentStyle.slice(1)}
                  </Badge>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-neutral-900">Communication Style</p>
                  <Badge variant="outline">
                    {profile.communication.style.charAt(0).toUpperCase() + profile.communication.style.slice(1)}
                  </Badge>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-neutral-900">Social Energy</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-neutral-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-violet-600 rounded-full"
                        style={{ width: `${profile.lifestyle.socialPreference}%` }}
                      />
                    </div>
                    <span className="text-xs text-neutral-600">
                      {profile.lifestyle.socialPreference > 50 ? 'Extroverted' : 'Introverted'}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-neutral-900">Activity Level</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-neutral-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-violet-600 rounded-full"
                        style={{ width: `${profile.lifestyle.activityLevel}%` }}
                      />
                    </div>
                    <span className="text-xs text-neutral-600">
                      {profile.lifestyle.activityLevel > 50 ? 'Adventurous' : 'Homebody'}
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Core Values */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Heart className="w-5 h-5 text-pink-600" />
                <h3 className="font-medium">Your Core Values</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.values.coreValues.map((value: string) => (
                  <Badge key={value} variant="secondary">
                    {formatValue(value)}
                  </Badge>
                ))}
              </div>
            </Card>

            {/* Life Goals */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Target className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium">Life Goals</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.values.lifeGoals.map((goal: string) => (
                  <Badge key={goal} variant="outline">
                    {formatGoal(goal)}
                  </Badge>
                ))}
              </div>
            </Card>

            {/* Attachment Guidance */}
            <Card className="p-6 space-y-3 bg-violet-50 border-violet-200">
              <div className="flex items-center gap-3">
                <Brain className="w-5 h-5 text-violet-600" />
                <h3 className="font-medium text-violet-900">Attachment Guidance</h3>
              </div>
              <p className="text-sm text-violet-800">
                {insights.attachmentGuidance}
              </p>
            </Card>

            {/* Upgrade CTA */}
            {!isPremium && (
              <Card className="p-6 bg-gradient-to-br from-violet-600 to-purple-600 text-white">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Crown className="w-6 h-6" />
                  </div>
                  <div className="flex-1 space-y-3">
                    <div>
                      <h3 className="font-medium text-lg">Unlock Premium Singles Features</h3>
                      <p className="text-sm text-violet-100 mt-1">
                        Get advanced alignment analysis, connection strategy guides, and personalized action plans
                      </p>
                    </div>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                        <span>Deep alignment scoring with potential connections</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                        <span>Personalized connection strategy based on your patterns</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                        <span>Red flag detection and green flag checklist</span>
                      </li>
                    </ul>
                    <Button
                      onClick={() => navigate('/pricing')}
                      className="bg-white text-violet-600 hover:bg-neutral-100"
                    >
                      Upgrade Now
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Ideal Partner Tab */}
        {activeTab === 'ideal-partner' && (
          <div className="space-y-6">
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-violet-600" />
                <div>
                  <h2 className="text-lg font-medium">Ideal Relationship Traits</h2>
                  <p className="text-sm text-neutral-600">Ranked by importance to you</p>
                </div>
              </div>

              <div className="space-y-4">
                {Object.entries(profile.idealPartner)
                  .sort(([, a]: any, [, b]: any) => b - a)
                  .map(([trait, importance]: [string, any]) => {
                    const labels: Record<string, string> = {
                      emotionalIntelligence: 'Emotional Intelligence',
                      communication: 'Communication Skills',
                      sharedValues: 'Shared Core Values',
                      stability: 'Emotional Stability',
                      humor: 'Sense of Humor',
                      ambition: 'Career Ambition',
                      adventure: 'Adventurousness',
                      physicalAttraction: 'Physical Attraction',
                    };

                    return (
                      <div key={trait} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-neutral-900">
                            {labels[trait]}
                          </span>
                          <span className="text-sm text-violet-600 font-medium">
                            {importance}%
                          </span>
                        </div>
                        <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              importance >= 70
                                ? 'bg-violet-600'
                                : importance >= 40
                                ? 'bg-violet-400'
                                : 'bg-neutral-400'
                            }`}
                            style={{ width: `${importance}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </Card>

            <Card className="p-6 space-y-4 bg-blue-50 border-blue-200">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium text-blue-900">Your Top 3 Priorities</h3>
              </div>
              <ul className="space-y-2 text-sm text-blue-800">
                {insights.topPriorities.map((priority: string) => {
                  const labels: Record<string, string> = {
                    emotionalIntelligence: 'Emotional Intelligence',
                    communication: 'Communication Skills',
                    sharedValues: 'Shared Core Values',
                    stability: 'Emotional Stability',
                    humor: 'Sense of Humor',
                    ambition: 'Career Ambition',
                    adventure: 'Adventurousness',
                    physicalAttraction: 'Physical Attraction',
                  };
                  return (
                    <li key={priority} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600" />
                      {labels[priority]}
                    </li>
                  );
                })}
              </ul>
            </Card>

            {/* Locked Premium Content */}
            {!isPremium && (
              <Card className="p-6 bg-neutral-100 border-neutral-300 relative overflow-hidden">
                <div className="absolute inset-0 backdrop-blur-sm bg-white/50 flex items-center justify-center">
                  <div className="text-center space-y-3">
                    <Lock className="w-8 h-8 text-neutral-600 mx-auto" />
                    <div>
                      <p className="font-medium text-neutral-900">Premium Feature</p>
                      <p className="text-sm text-neutral-600">
                        Unlock detailed alignment analysis
                      </p>
                    </div>
                    <Button onClick={() => navigate('/pricing')} size="sm">
                      Upgrade to Premium
                    </Button>
                  </div>
                </div>
                <div className="opacity-30 space-y-3">
                  <h3 className="font-medium">Aligned Partner Dynamics</h3>
                  <p className="text-sm text-neutral-600">
                    Based on your profile, you are most likely to align with...
                  </p>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Red & Green Flags Tab */}
        {activeTab === 'red-flags' && (
          <div className="space-y-6">
            {/* Green Flags */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
                <div>
                  <h2 className="text-lg font-medium">Green Flags to Look For</h2>
                  <p className="text-sm text-neutral-600">Positive signs of healthy alignment</p>
                </div>
              </div>

              <div className="space-y-3">
                {insights.greenFlags.length > 0 ? (
                  insights.greenFlags.map((flag: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-green-900">{flag}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-neutral-600">
                    Complete more of your profile to generate personalized green flags
                  </p>
                )}

                {/* Default green flags based on values */}
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-900">
                    Consistent communication that matches your preferred style
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-900">
                    Alignment on core values: {profile.values.coreValues.slice(0, 2).map(formatValue).join(', ')}
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-900">
                    Matching life goals around {profile.values.lifeGoals.length > 0 ? formatGoal(profile.values.lifeGoals[0]).toLowerCase() : 'your future'}
                  </p>
                </div>
              </div>
            </Card>

            {/* Red Flags */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                <div>
                  <h2 className="text-lg font-medium">Red Flags to Watch For</h2>
                  <p className="text-sm text-neutral-600">Warning signs based on your patterns</p>
                </div>
              </div>

              <div className="space-y-3">
                {insights.redFlags.length > 0 ? (
                  insights.redFlags.map((flag: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-900">{flag}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-neutral-600">
                    No specific red flags identified based on your profile
                  </p>
                )}

                {/* Universal red flags */}
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-900">
                    Inconsistent behavior or mixed signals about intentions
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-900">
                    Disrespect for your boundaries or communication needs
                  </p>
                </div>
              </div>
            </Card>

            {/* Locked Premium Content */}
            {!isPremium && (
              <Card className="p-6 bg-neutral-100 border-neutral-300 relative overflow-hidden">
                <div className="absolute inset-0 backdrop-blur-sm bg-white/50 flex items-center justify-center">
                  <div className="text-center space-y-3">
                    <Lock className="w-8 h-8 text-neutral-600 mx-auto" />
                    <div>
                      <p className="font-medium text-neutral-900">Premium Feature</p>
                      <p className="text-sm text-neutral-600">
                        Get personalized red flag assessments
                      </p>
                    </div>
                    <Button onClick={() => navigate('/pricing')} size="sm">
                      Upgrade to Premium
                    </Button>
                  </div>
                </div>
                <div className="opacity-30 space-y-3">
                  <h3 className="font-medium">Personalized Warning Signs</h3>
                  <p className="text-sm text-neutral-600">
                    Based on your attachment style and past patterns...
                  </p>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Connection Strategy Tab */}
        {activeTab === 'strategy' && (
          <div className="space-y-6">
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Target className="w-6 h-6 text-violet-600" />
                <div>
                  <h2 className="text-lg font-medium">Your Connection Strategy</h2>
                  <p className="text-sm text-neutral-600">Personalized approach based on your profile</p>
                </div>
              </div>

              <div className="tension-glow bg-violet-50 border border-violet-200 rounded-lg p-4">
                <p className="text-sm text-violet-900">
                  {insights.datingStrategy}
                </p>
              </div>
            </Card>

            {/* Communication Tips */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium">Communication Approach</h3>
              </div>
              <div className="space-y-3">
                <p className="text-sm text-neutral-700">
                  Based on your <strong>{profile.communication.style}</strong> communication style:
                </p>
                {profile.communication.style === 'direct' && (
                  <ul className="space-y-2 text-sm text-neutral-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Be upfront about your intentions early on</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Look for partners who appreciate directness</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Practice patience with those who communicate differently</span>
                    </li>
                  </ul>
                )}
                {profile.communication.style === 'balanced' && (
                  <ul className="space-y-2 text-sm text-neutral-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Adapt your style to match your partner's comfort level</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Be direct about important topics, subtle with emotions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Your flexibility is a strength - use it</span>
                    </li>
                  </ul>
                )}
              </div>
            </Card>

            {/* Action Items */}
            <Card className="p-6 space-y-4 bg-green-50 border-green-200">
              <h3 className="font-medium text-green-900">Next Steps</h3>
              <ul className="space-y-3 text-sm text-green-800">
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">
                    1
                  </span>
                  <span>Review your core values and ensure they are reflected in how you show up in new connections</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">
                    2
                  </span>
                  <span>Practice recognizing red flags from your past patterns early in new interactions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">
                    3
                  </span>
                  <span>Focus on {insights.topPriorities[0]} when evaluating potential partners</span>
                </li>
              </ul>
            </Card>

            {/* Premium Upgrade */}
            {!isPremium && (
              <Card className="p-6 bg-gradient-to-br from-violet-600 to-purple-600 text-white">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Crown className="w-6 h-6" />
                    <h3 className="font-medium text-lg">Unlock Advanced Connection Strategy</h3>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Personalized first-meet conversation starters</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Connection profile optimization based on your values</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>When to have "the talk" based on your attachment style</span>
                    </li>
                  </ul>
                  <Button
                    onClick={() => navigate('/pricing')}
                    className="bg-white text-violet-600 hover:bg-neutral-100"
                  >
                    View Premium Plans
                  </Button>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 pt-6">
          <Card 
            className="p-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/match-calculator')}
          >
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <Scale className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-neutral-900">Alignment Calculator</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Calculate alignment with someone
                </p>
              </div>
              <Button variant="outline" size="sm" className="w-full">
                Calculate Alignment
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>

          <Card 
            className="p-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/date-journal')}
          >
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-neutral-900">Connection Journal</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Track interactions and observe patterns
                </p>
              </div>
              <Button variant="outline" size="sm" className="w-full">
                View Journal
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>

          <Card 
            className="p-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/singles-packs')}
          >
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto">
                <Sparkles className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <h3 className="font-medium text-neutral-900">Singles Packs</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Unlock premium relationship tools
                </p>
              </div>
              <Button variant="outline" size="sm" className="w-full">
                Explore Packs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
